'use client';

/**
 * Lazy wrapper for sonner's `<Toaster />` (BS-2161).
 *
 * Sonner bundles its own ~68 KB ESM module. Mounting `<Toaster />` directly
 * in `main-layout.tsx` pulls the whole sonner runtime into the shared layout
 * chunk for every route - including `/`, `/_not-found`, and `/gsap`, which
 * never call `toast()`.
 *
 * Strategy: load the Toaster on the client only, after first paint, via
 * `useEffect` + dynamic import. `toast.*` calls elsewhere in the app (which
 * still import from `'sonner'`) keep working - sonner's toast queue buffers
 * calls fired before the Toaster mounts, so the user-visible behavior is
 * identical aside from a ~1-frame delay on the very first toast.
 *
 * Sonner is hoisted into its own dynamic chunk; subsequent route loads
 * reuse it from the browser cache.
 */

import { useEffect, useState, type ComponentType } from 'react';

type ToasterComponent = ComponentType<{
  position?:
    | 'top-left'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-right'
    | 'top-center'
    | 'bottom-center';
  toastOptions?: { duration?: number };
}>;

export function ToasterLazy() {
  const [Toaster, setToaster] = useState<ToasterComponent | null>(null);

  useEffect(() => {
    let cancelled = false;
    void import('sonner').then((mod) => {
      if (!cancelled) {
        setToaster(() => mod.Toaster as ToasterComponent);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!Toaster) return null;
  return <Toaster position="bottom-right" toastOptions={{ duration: 5000 }} />;
}
